// Empire Apps Chassis · connectors/health · shared contract
// Health connectors mirror the govt-fork ConnectorAdapter shape so that the
// host folio can drive both with one router. Health adds a PHI dimension on
// every consent prompt (DPDP 2023 highest-sensitivity sub-category).

export type ConnectorMode = 'test' | 'live';

export interface ConnectorStatus {
  readonly id: string;
  readonly mode: ConnectorMode;
  readonly connected: boolean;
  readonly lastSyncAt: string | null;
  readonly principalIdHash: string | null;
  readonly notes: string;
}

export interface ConnectorError {
  readonly code:
    | 'not-configured'
    | 'auth-cancelled'
    | 'consent-missing'
    | 'network'
    | 'parse'
    | 'signature'
    | 'rate-limited'
    | 'unsupported'
    | 'live-disabled'
    | 'native-shell-required'
    | 'bluetooth-unavailable';
  readonly message: string;
  readonly retriable: boolean;
}

export type ConnectorResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ConnectorError };

export interface ConnectorAdapter<TStatus extends ConnectorStatus, TPayload> {
  readonly id: string;
  readonly displayName: string;
  readonly displayNameHi: string;
  readonly dpdpCategory: string;
  readonly mode: ConnectorMode;
  getStatus(): Promise<TStatus>;
  connect(): Promise<ConnectorResult<TStatus>>;
  disconnect(): Promise<void>;
  importPayload(): Promise<ConnectorResult<TPayload>>;
  mockPayload(): TPayload;
}

export const ok = <T>(value: T): ConnectorResult<T> => ({ ok: true, value });
export const err = <T>(error: ConnectorError): ConnectorResult<T> => ({ ok: false, error });

export function notConfigured(message: string): ConnectorError {
  return { code: 'not-configured', message, retriable: false };
}

export function liveDisabled(connectorId: string): ConnectorError {
  return {
    code: 'live-disabled',
    message: `${connectorId} live mode is gated behind DEV flag and requires production credentials.`,
    retriable: false,
  };
}

export function nativeShellRequired(connectorId: string): ConnectorError {
  return {
    code: 'native-shell-required',
    message: `${connectorId} requires the native Capacitor shell (HealthKit / native bridge unavailable in web view).`,
    retriable: false,
  };
}

export function isOk<T>(r: ConnectorResult<T>): r is { ok: true; value: T } {
  return r.ok;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export async function shortHash(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  let s = '';
  const bytes = new Uint8Array(buf);
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).slice(0, 16);
}

export function devModeEnabled(): boolean {
  const g = globalThis as unknown as { __EMPIRE_CONNECTORS_DEV__?: boolean };
  return g.__EMPIRE_CONNECTORS_DEV__ === true;
}

export async function fetchWithBudget(
  url: string,
  init: RequestInit,
  opts: { readonly retries: number; readonly timeoutMs: number }
): Promise<Response> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= opts.retries; attempt++) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), opts.timeoutMs);
    try {
      const res = await fetch(url, { ...init, signal: ctl.signal });
      clearTimeout(timer);
      if (res.status >= 500 && attempt < opts.retries) {
        await sleep(250 * (attempt + 1));
        continue;
      }
      return res;
    } catch (e) {
      clearTimeout(timer);
      lastErr = e;
      if (attempt >= opts.retries) break;
      await sleep(250 * (attempt + 1));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('fetch-failed');
}

export function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

// Shared FHIR R4 minimal types for ABDM PHR ingest.
export interface FhirCoding {
  readonly system: string;
  readonly code: string;
  readonly display?: string;
}

export interface FhirObservation {
  readonly resourceType: 'Observation';
  readonly id: string;
  readonly status: 'final' | 'preliminary' | 'amended';
  readonly code: { readonly coding: readonly FhirCoding[]; readonly text?: string };
  readonly effectiveDateTime: string;
  readonly valueQuantity?: { readonly value: number; readonly unit: string; readonly system?: string; readonly code?: string };
  readonly valueString?: string;
  readonly referenceRange?: readonly { readonly low?: { readonly value: number; readonly unit: string }; readonly high?: { readonly value: number; readonly unit: string }; readonly text?: string }[];
}

export interface FhirMedicationRequest {
  readonly resourceType: 'MedicationRequest';
  readonly id: string;
  readonly status: 'active' | 'completed' | 'stopped';
  readonly intent: 'order' | 'plan';
  readonly medicationCodeableConcept: { readonly coding: readonly FhirCoding[]; readonly text: string };
  readonly authoredOn: string;
  readonly dosageInstruction?: readonly { readonly text: string; readonly timing?: { readonly code?: { readonly text: string } } }[];
  readonly dispenseRequest?: { readonly quantity?: { readonly value: number; readonly unit: string }; readonly expectedSupplyDuration?: { readonly value: number; readonly unit: 'd' } };
}

export interface FhirImmunization {
  readonly resourceType: 'Immunization';
  readonly id: string;
  readonly status: 'completed';
  readonly vaccineCode: { readonly coding: readonly FhirCoding[]; readonly text: string };
  readonly occurrenceDateTime: string;
  readonly lotNumber?: string;
  readonly site?: { readonly text: string };
  readonly performer?: readonly { readonly actor: { readonly display: string } }[];
}

export type FhirResource = FhirObservation | FhirMedicationRequest | FhirImmunization;

export interface FhirBundle {
  readonly resourceType: 'Bundle';
  readonly type: 'collection';
  readonly entry: readonly { readonly resource: FhirResource }[];
}

// Lab test name normalisation. Indian labs spell the same panel six ways.
export const LAB_TEST_ALIASES: Readonly<Record<string, readonly string[]>> = Object.freeze({
  hba1c: ['hba1c', 'glycated haemoglobin', 'glycosylated hemoglobin', 'a1c', 'hb a1c'],
  cbc: ['complete blood count', 'cbc', 'haemogram', 'hemogram'],
  lft: ['liver function test', 'lft', 'liver panel'],
  kft: ['kidney function test', 'kft', 'renal panel', 'rft', 'renal function'],
  lipid: ['lipid profile', 'lipid panel', 'cholesterol panel'],
  tsh: ['tsh', 'thyroid stimulating hormone', 'thyroid panel'],
  vit_d: ['vitamin d', 'vit d', '25-hydroxy vitamin d', '25-oh-d'],
  b12: ['vitamin b12', 'vit b12', 'b12', 'cobalamin'],
  ferritin: ['ferritin', 'serum ferritin'],
  hscrp: ['hs-crp', 'high sensitivity crp', 'cardiac crp'],
  blood_sugar: ['fasting blood sugar', 'fbs', 'random blood sugar', 'rbs', 'ppbs', 'post prandial', 'glucose'],
  uric_acid: ['uric acid', 'serum uric acid'],
});

export function normaliseLabTest(raw: string): string | null {
  const lc = raw.toLowerCase().trim();
  for (const [canonical, aliases] of Object.entries(LAB_TEST_ALIASES)) {
    if (aliases.some((a) => lc.includes(a))) return canonical;
  }
  return null;
}
